import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {InputAdornment, Stack, TextField, Typography} from "@mui/material";
import {useQuery} from "@apollo/client";
import {DELETE_TRANSLATION_ITEMS, GET_MODULES, GET_PHRASE_ITEMS, GET_TRANSLATION_ITEMS, PUT_TRANSLATION_ITEM} from "./Queries";
import {graphQLServices} from "../../services/GraphQLServices";
import {ModalLoader} from "../../components/common/ModalLoader";
import { Module } from "./models/Module";
import {PhraseItem} from "./models/PhraseItem";
import {TranslationItem} from "./models/TranslationItem";
import {LazyAccordion} from "../../components/common/containers/LazyAccordion";
import {Delete, ExpandMore, Search} from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {Instant} from "@js-joda/core";

const stage = "kizidev";

export const TranslationApprovalQueue = pageAuthorizedByGroup(function() {
	const [search, setSearch] = useState("");
	
	const moduleQuery = useQuery(GET_MODULES, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			first: 10_000,
			order: [
				{name: "ASC"}
			]
		}
	});
	
	const translationQuery = useQuery(GET_TRANSLATION_ITEMS, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			first: 10_000,
			where: {
				approvalDate: {eq: null}
			},
			order: [
				{moduleId: "ASC"}
			],
		}
	});
	
	// Only fetch the phrases referenced by the pending translations: all phrases no longer fit in one page.
	const pendingTranslations = translationQuery.data?.translationItems?.nodes as TranslationItem[] | undefined;
	const phraseIds = pendingTranslations
		? Array.from(new Set(pendingTranslations.map(it => it.phraseId)))
		: [];
	
	const phraseQuery = useQuery(GET_PHRASE_ITEMS, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		skip: !pendingTranslations,
		variables: {
			stage,
			first: Math.max(phraseIds.length, 1),
			where: {
				id: {in: phraseIds}
			},
			order: [
				{phrase: "ASC"}
			]
		}
	});
	
	const error = moduleQuery.error || phraseQuery.error || translationQuery.error;
	if (error)
		throw error;
	
	const loading = moduleQuery.loading || translationQuery.loading || phraseQuery.loading || !phraseQuery.data;
	if (loading)
		return <ModalLoader open={true} transparancy={0.1}/>;
	
	const modules = moduleQuery.data.modules.nodes as Module[];
	const phrases = phraseQuery.data.phraseItems.nodes as PhraseItem[];
	const translations = pendingTranslations ?? [];
	
	const moduleIndex = modules.associateBy(it => it.id);
	const phraseIndex = phrases.associateBy(it => it.id);
	
	translations.forEach(it => {
		it.module = moduleIndex.get(it.moduleId)?.name;
		it.phrase = phraseIndex.get(it.phraseId)?.phrase;
	});
	
	const filteredTranslations = translations.filter(it => {
		if (!search)
			return true;
		
		const lowerSearch = search.toLowerCase();
		
		if (it.module && it.module.toLowerCase().indexOf(lowerSearch) >= 0)
			return true;
		
		if (it.phrase && it.phrase.toLowerCase().indexOf(lowerSearch) >= 0)
			return true;
		
		return it.translation.toLowerCase().indexOf(lowerSearch) >= 0;
	});
	
	const translationsByModule = filteredTranslations.associateManyBy(it => it.moduleId);
	const items = Array.from(translationsByModule, ([name, value]) =>
		({ name, value: value.sort(compareTranslationItems) }));
	
	const count = search
		? filteredTranslations.length + " / " + translations.length
		: translations.length;
	
	return <Stack gap={1} style={{paddingBottom: "300px"}}>
		<Typography variant={"h5"}>Approval Queue ({count})</Typography>
		
		<TextField
			placeholder={"Search"}
			value={search}
			onChange={event => setSearch(event.target.value)}
			InputProps={{
				startAdornment: <InputAdornment position={"start"}>
					<Search/>
				</InputAdornment>,
			}}
		/>
		
		{items.map(item =>
			<TranslationModuleComponent
				key={item.name}
				id={item.name}
				items={item.value}
				moduleIndex={moduleIndex}
				phraseIndex={phraseIndex}
			/>
		)}
	</Stack>;
}, ["Admin", "Translations"]);

interface TranslationModuleComponentProps {
	readonly id: string;
	readonly items: TranslationItem[];
	readonly moduleIndex: Map<string, Module>;
	readonly phraseIndex: Map<string, PhraseItem>;
}

function TranslationModuleComponent(props: TranslationModuleComponentProps) {
	const name = props.moduleIndex.get(props.id)?.name ?? props.id;
	
	return <LazyAccordion
		summaryProps={{expandIcon: <ExpandMore/>}}
		summary={<Typography variant={"subtitle1"}>{name} ({props.items.length})</Typography>}
	>
		{props.items.map(item => <TranslationItemComponent
			key={item.id}
			item={item}
			phraseIndex={props.phraseIndex}
		/>)}
	</LazyAccordion>;
}

interface TranslationItemComponentProps {
	readonly item: TranslationItem;
	readonly phraseIndex: Map<string, PhraseItem>;
}

function TranslationItemComponent(props: TranslationItemComponentProps) {
	const item = props.item;
	const phrase = props.phraseIndex.get(item.phraseId);
	
	const [translation, setTranslation] = useState(item.translation);
	const [approvedAt, setApprovedAt] = useState(0);
	const [deletedAt, setDeletedAt] = useState(0);
	
	async function approveItem() {
		await graphQLServices.translationsClient.mutate({
			mutation: PUT_TRANSLATION_ITEM,
			variables: {
				stage,
				translation: {
					id: item.id,
					version: item.version,
					moduleId: item.moduleId,
					phraseId: item.phraseId,
					languageCode: item.languageCode,
					translation: translation,
					approvalDate: Instant.now().toString(),
				},
			},
		});
		setApprovedAt(performance.now());
	}
	
	async function deleteItem() {
		await graphQLServices.translationsClient.mutate({
			mutation: DELETE_TRANSLATION_ITEMS,
			variables: {
				stage,
				translationItemIds: [item.id],
			},
		});
		setDeletedAt(performance.now());
	}
	
	return <Paper elevation={2} style={{padding: "4px 8px", margin: "8px 0"}}>
		<Stack direction={"row"} gap={1} alignItems={"start"}>
			<Stack style={{width: "60px", minWidth: "60px"}}>
				<Typography>en-US:</Typography>
				<Typography>{item.languageCode}:</Typography>
			</Stack>
			<Stack style={{flexGrow: 1}}>
				<Typography>{phrase?.phrase ?? "null"}</Typography>
				<TextField
					size={"small"}
					value={translation}
					onChange={event => setTranslation(event.target.value)}
					multiline
				/>
				{item.locationUrl && (
					<div>
						<a
							href={item.locationUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ display: "block", marginTop: "8px" }}
						>
							{item.locationUrl}
						</a>
					</div>
				)}
			</Stack>
			<Stack style={{flexShrink: 0}} gap={1} alignItems={"stretch"}>
				<ProcessButton
					defaultText={"Approve"}
					completedText={"Approved"}
					inProgressText={"Approving"}
					variant={"outlined"}
					defaultColor={"info"}
					onClick={approveItem}
					resetKeys={[translation, deletedAt]}
				/>
				<ProcessButton
					startIcon={<Delete/>}
					defaultText={"Delete"}
					completedText={"Deleted"}
					inProgressText={"Deleting"}
					variant={"outlined"}
					defaultColor={"warning"}
					onClick={deleteItem}
					resetKeys={[translation, approvedAt]}
				/>
			</Stack>
		</Stack>
	</Paper>;
}

function compareTranslationItems(left: TranslationItem, right: TranslationItem): number {
	const l = compareIgnoreCase(left.languageCode, right.languageCode);
	if (l !== 0)
		return l;
	
	return compareIgnoreCase(left.phrase ?? left.phraseId, right.phrase ?? right.phraseId);
}

function compareIgnoreCase(left: string, right: string): number {
	return left.toLowerCase().localeCompare(right.toLowerCase());
}
